import { useState, useMemo, useCallback } from "react";

export type SortDirection = "asc" | "desc" | null;

export interface TableSortState {
  field: string | null;
  direction: SortDirection;
}

export interface UseTableSortReturn<T> {
  sortState: TableSortState;
  handleSort: (field: string) => void;
  clearSort: () => void;
  sortedData: T[];
  isSorted: boolean;
}

function getNestedValue(obj: any, path: string): any {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

export function useTableSort<T = any>(
  data: T[],
  defaultField?: string,
  defaultDirection: SortDirection = "asc"
): UseTableSortReturn<T> {
  const [sortState, setSortState] = useState<TableSortState>({
    field: defaultField || null,
    direction: defaultField ? defaultDirection : null,
  });

  const handleSort = useCallback((field: string) => {
    setSortState((prev) => {
      if (prev.field === field) {
        if (prev.direction === "asc") return { field, direction: "desc" };
        if (prev.direction === "desc") return { field: null, direction: null };
        return { field, direction: "asc" };
      }
      return { field, direction: "asc" };
    });
  }, []);

  const clearSort = useCallback(() => {
    setSortState({ field: null, direction: null });
  }, []);

  const sortedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return data ?? ([] as T[]);
    if (!sortState.field || !sortState.direction) return data;

    return [...data].sort((a, b) => {
      const aVal = getNestedValue(a, sortState.field!);
      const bVal = getNestedValue(b, sortState.field!);

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      let comparison = 0;
      if (typeof aVal === "string" && typeof bVal === "string") {
        comparison = aVal.localeCompare(bVal, "es", { sensitivity: "base" });
      } else if (typeof aVal === "number" && typeof bVal === "number") {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal), "es", { sensitivity: "base" });
      }

      return sortState.direction === "desc" ? -comparison : comparison;
    });
  }, [data, sortState]);

  return {
    sortState,
    handleSort,
    clearSort,
    sortedData,
    isSorted: !!sortState.field,
  };
}
